import React from "react";
import {
  Stack,
  Title,
  Text,
  Paper,
  Group,
  Button,
  Divider,
  Loader,
} from "@mantine/core";
import { useResponsive } from "../../../../../context/ResponsiveContext";
import { QuickSaleProductState } from "../../../../../types/quickSale";
import { BusinessInfo } from "../../../../../types/context";
import { GetTransactionsResponse } from "../../../../../types/transactions";
import moment from "moment";
import { capitalizeFirstLetter } from "../../../../../utils/stringUtils";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import { computeTransactionTotals } from "../../../../../utils/transactionTotals";

interface QuickSaleSummaryProps {
  transaction: GetTransactionsResponse;
  products: QuickSaleProductState[];
  subtotal: number;
  discounts: number;
  total: number;
  salesTax: number;
  totalWithTax: number;
  paidAmount: number;
  remainingBalance: number;
  onCheckout: () => void;
  onClose: () => void;
  isLoading: boolean;
  isTaxesLoading?: boolean;
  error: string | null;
  businessInfo: BusinessInfo | null;
  isViewOnly?: boolean;
}

export default function QuickSaleSummary({
  transaction,
  products,
  subtotal,
  discounts,
  total,
  salesTax,
  totalWithTax,
  paidAmount,
  remainingBalance,
  onCheckout,
  onClose,
  isLoading,
  isTaxesLoading = false,
  error,
  businessInfo,
  isViewOnly = false,
}: QuickSaleSummaryProps) {
  const { isMobile } = useResponsive();

  // A gift-card line item lives on the real transaction and is billable on its
  // own. Once a product has created the sale and is then removed, the gift card
  // still carries an outstanding balance that must be checkoutable — otherwise
  // it can never be paid for and never mints. So gate checkout on "no products
  // AND no gift-card line items", not products alone.
  const giftCardCount = transaction?.line_items?.gift_cards?.length ?? 0;

  // Format amount helper — keeps the "0 renders as a dash" behavior while
  // delegating the actual money formatting to formatCurrency.
  const formatAmount = (amount: number): string => {
    return amount === 0 ? "-" : formatCurrency(amount);
  };

  // Phase 2: the quick sale transaction is created on the FIRST product add, so
  // calc__taxes and line_item_taxes are available immediately after the first
  // product lands in the cart. Source the per-rate breakdown from the single
  // source of truth (computeTransactionTotals) so the summary and the checkout
  // screen render identical tax rows; empty until the transaction exists.
  const taxBreakdown = transaction
    ? computeTransactionTotals(transaction).taxBreakdown
    : [];

  return (
    <Stack p="md">
      {/* Header */}
      <Group justify="space-between" align="flex-start">
        <Title order={4}>Order Summary</Title>
        {!isMobile && (
          <Button
            variant="subtle"
            size="sm"
            onClick={onClose}
            px={12}
            style={{ marginTop: "-8px", marginRight: "-8px" }}
          >
            ✕
          </Button>
        )}
      </Group>

      <Paper style={{ backgroundColor: "inherit" }}>
        <Stack gap="md">
          {/* Subtotal */}
          <Group justify="space-between">
            <Text fw={500}>Subtotal</Text>
            <Text fw={500}>{formatAmount(subtotal)}</Text>
          </Group>

          {/* Discounts */}
          {discounts > 0 && (
            <Group justify="space-between">
              <Text>Discounts</Text>
              <Text c="red" fw={500}>
                -{formatAmount(discounts)}
              </Text>
            </Group>
          )}

          {/* Per-rate sales tax breakdown, sourced from the freshly-created
              quick sale transaction's line_items.taxes arrays. While the
              backend recomputes taxes (add/update/remove line item), show an
              inline loader so the operator knows the amounts are refreshing. */}
          {isTaxesLoading ? (
            <Group justify="space-between">
              <Group gap="xs">
                <Loader size="xs" />
                <Text c="dimmed">Calculating taxes…</Text>
              </Group>
            </Group>
          ) : (
            taxBreakdown.length > 0 && (
              <Stack gap="xs">
                {taxBreakdown.map((tax) => (
                  <Group justify="space-between" key={tax.id}>
                    <Text>
                      {tax.name} ({(tax.rate * 100).toFixed(3)}%)
                    </Text>
                    <Text fw={500}>{formatAmount(tax.amount)}</Text>
                  </Group>
                ))}
              </Stack>
            )
          )}

          <Divider />

          {/* Total. While taxes are recomputing, totalWithTax still carries
              the previous tax values — avoid showing a number that doesn't
              match the "Calculating taxes…" row above it. */}
          <Group justify="space-between">
            <Text size="lg" fw={700}>
              Total
            </Text>
            {isTaxesLoading ? (
              <Loader size="xs" />
            ) : (
              <Text size="lg" fw={700} style={{ letterSpacing: "0.25px" }}>
                {formatAmount(totalWithTax).replace("$", "$ ")}
              </Text>
            )}
          </Group>
        </Stack>
      </Paper>

      {/* Payments Made */}
      {transaction?.payments?.some(
        (payment) => payment.status === "succeeded",
      ) && (
        <Paper pt="md" style={{ backgroundColor: "inherit" }}>
          <Title order={5} mb="xs">
            Payments Made
          </Title>
          <Stack gap="sm">
            {transaction?.payments
              .filter((payment) => payment.status === "succeeded")
              .map((payment) => (
                <Group key={payment.id} justify="space-between">
                  <Stack gap={2}>
                    <Text size="sm">
                      {moment.utc(payment.created_dt).format("M/D/YY")} -{" "}
                      {capitalizeFirstLetter(payment.type)} (
                      {capitalizeFirstLetter(payment.method)})
                    </Text>
                  </Stack>
                  <Text fw={500} c="red">
                    {formatCurrency(-payment.amount)}
                  </Text>
                </Group>
              ))}
          </Stack>
        </Paper>
      )}

      <Divider />

      {remainingBalance > 0.01 && (
        <Paper pb="md" style={{ backgroundColor: "inherit" }}>
          <Group justify="space-between">
            <Text fw={500}>Remaining Balance</Text>
            {isTaxesLoading ? (
              <Loader size="xs" />
            ) : (
              <Text fw={500} size="lg">
                {formatAmount(remainingBalance)}
              </Text>
            )}
          </Group>
        </Paper>
      )}

      {/* Checkout Button or Completion Message */}
      {(!isViewOnly || remainingBalance > 0.01) && (
        <>
          <Button
            onClick={onCheckout}
            disabled={
              (products.length === 0 && giftCardCount === 0) ||
              isLoading ||
              isTaxesLoading
            }
            size="lg"
            fullWidth
            color="var(--color-primary)"
          >
            Checkout
          </Button>

          {error && (
            <Text size="sm" c="red" ta="center">
              {error}
            </Text>
          )}
        </>
      )}
    </Stack>
  );
}
